import React, { useState, useEffect, useCallback } from 'react';
import { klineMonitorService } from '../../services/klineMonitorService';
import { markdownToHtml } from '../../utils/markdownUtils';
import ContentDialog from '../ContentDialog';
import { getSignalLabel } from '../../services/klineTypes';
import { LogExportFormat, LogSource } from '@/services/enums';

interface MonitorLogsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface LogItemProps {
  log: any;
  onOpenDetail: (title: string, content: string, type: LogExportFormat, extra?: any) => void;
}

const LogItem = ({ log, onOpenDetail }: LogItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const [promptTab, setPromptTab] = useState<LogSource>('combined');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-400/10 border-green-400/20';
      case 'failed':
        return 'text-red-400 bg-red-400/10 border-red-400/20';
      default:
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    }
  };

  return (
    <div
      className={`border rounded-xl transition-all duration-200 ${expanded ? 'bg-white/5 border-white/20 shadow-lg' : 'bg-transparent border-white/10 hover:bg-white/5'}`}
    >
      <div className="p-4 flex flex-col gap-3 cursor-pointer select-none" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <span className="font-bold text-white text-sm tracking-wide truncate block" title={log.symbol}>
              {log.symbol}
            </span>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-white/10 text-white/60 border border-white/5 whitespace-nowrap">
              {log.bar}
            </span>
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(log.status)} whitespace-nowrap`}
            >
              {log.status}
            </span>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${expanded ? 'bg-white/10 text-white' : 'text-white/40'}`}
            >
              <svg
                className={`w-5 h-5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-white/40 font-mono border-t border-white/5 pt-2">
          <span>{new Date(log.createdAt).toLocaleString()}</span>
          <span>{new Date(log.updatedAt).toLocaleString()}</span>
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-white/10 pt-5">
          {log.error && (
            <div className="mb-5 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm flex gap-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <div className="font-semibold mb-1">发生错误</div>
                <div className="opacity-90">{log.error}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Prompt Details */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-blue-300/80 uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                  提示词
                </div>
                <div className="flex items-center gap-2">
                  {log.systemPrompt && (
                    <div className="flex bg-white/5 rounded-lg p-0.5 border border-white/10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPromptTab('system');
                        }}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                          promptTab === 'system'
                            ? 'bg-blue-500/20 text-blue-300 shadow-sm'
                            : 'text-white/40 hover:text-white/60'
                        }`}
                      >
                        系统
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPromptTab('user');
                        }}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                          promptTab === 'user'
                            ? 'bg-blue-500/20 text-blue-300 shadow-sm'
                            : 'text-white/40 hover:text-white/60'
                        }`}
                      >
                        用户
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPromptTab('combined');
                        }}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                          promptTab === 'combined'
                            ? 'bg-blue-500/20 text-blue-300 shadow-sm'
                            : 'text-white/40 hover:text-white/60'
                        }`}
                      >
                        完整
                      </button>
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDetail('提示词详情', log.userPrompt || '', 'prompt', {
                        systemPrompt: log.systemPrompt,
                        userPrompt: log.userPrompt,
                      });
                    }}
                    className="p-1 hover:bg-white/10 rounded text-white/40 hover:text-white transition-colors"
                    title="查看详情"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="bg-black/40 rounded-lg p-4 text-xs text-white/80 font-mono h-[300px] overflow-y-auto border border-white/10 shadow-inner scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                {(() => {
                  const content = log.systemPrompt
                    ? promptTab === 'combined'
                      ? `${log.systemPrompt}\n\n----------\n\n${log.userPrompt || ''}`
                      : promptTab === 'system'
                        ? log.systemPrompt
                        : log.userPrompt || ''
                    : log.userPrompt || '';

                  return content ? (
                    <div className="markdown-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }} />
                  ) : (
                    <span className="text-white/30 italic">暂无提示词</span>
                  );
                })()}
              </div>
            </div>

            {/* Analysis Report */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-purple-300/80 uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  分析报告
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenDetail('分析报告', log.analysisReport || '', 'markdown');
                  }}
                  className="p-1 hover:bg-white/10 rounded text-white/40 hover:text-white transition-colors"
                  title="查看详情"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                    />
                  </svg>
                </button>
              </div>
              <div className="bg-black/40 rounded-lg p-4 text-xs text-white/80 font-mono h-[300px] overflow-y-auto border border-white/10 shadow-inner scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                {log.analysisReport ? (
                  <div
                    className="markdown-content"
                    dangerouslySetInnerHTML={{ __html: markdownToHtml(log.analysisReport) }}
                  />
                ) : (
                  <span className="text-white/30 italic">暂无分析报告</span>
                )}
              </div>
            </div>

            {/* Monitor Rules */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-green-300/80 uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    />
                  </svg>
                  生成的规则
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenDetail('生成的规则', JSON.stringify(log.monitorRules, null, 2), 'json');
                  }}
                  className="p-1 hover:bg-white/10 rounded text-white/40 hover:text-white transition-colors"
                  title="查看详情"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                    />
                  </svg>
                </button>
              </div>
              <div className="bg-black/40 rounded-lg p-4 text-xs text-green-300/90 font-mono h-[300px] overflow-y-auto border border-white/10 shadow-inner scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                {log.monitorRules ? (
                  <pre className="whitespace-pre-wrap break-all font-mono text-xs leading-relaxed">
                    {JSON.stringify(log.monitorRules, null, 2)}
                  </pre>
                ) : (
                  <span className="text-white/30 italic">暂无生成规则</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MonitorLogsDialog: React.FC<MonitorLogsDialogProps> = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [extractedTypes, setExtractedTypes] = useState<string[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [filterUndefined, setFilterUndefined] = useState(false);
  const [detailDialog, setDetailDialog] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    systemPrompt?: string;
    userPrompt?: string;
    useMarkdown?: boolean;
  }>({
    isOpen: false,
    title: '',
    content: '',
  });

  const [showCopyToast, setShowCopyToast] = useState(false);

  const handleCopyAll = () => {
    let typesToProcess = extractedTypes;
    if (filterUndefined) {
      typesToProcess = extractedTypes.filter((type) => {
        const label = getSignalLabel(type);
        return label === type || label === '-';
      });
    }

    const text = typesToProcess.map((type) => `${type}: ${getSignalLabel(type)}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    });
  };

  const handleExtractSignalTypes = async () => {
    setExtracting(true);
    try {
      const BATCH_SIZE = 100;
      const firstPage = await klineMonitorService.getMonitorRuleLogs(1, BATCH_SIZE);
      const total = firstPage.total;
      const totalPages = Math.ceil(total / BATCH_SIZE);

      let allLogs = [...firstPage.logs];

      const promises: Promise<{ logs: any[]; total: number }>[] = [];
      for (let i = 2; i <= totalPages; i++) {
        promises.push(klineMonitorService.getMonitorRuleLogs(i, BATCH_SIZE));
      }

      if (promises.length > 0) {
        const results = await Promise.all(promises);
        results.forEach((res) => {
          allLogs = allLogs.concat(res.logs);
        });
      }

      const typesSet = new Set<string>();
      allLogs.forEach((log) => {
        if (!log.monitorRules) return;

        // Handle potentially different structures
        const rules = log.monitorRules;
        const signals = [...(rules.buySignals || []), ...(rules.sellSignals || [])];

        signals.forEach((signal: any) => {
          if (signal.signalType) {
            typesSet.add(signal.signalType);
          }
        });
      });

      setExtractedTypes(Array.from(typesSet).sort());
      setExtractDialogOpen(true);
    } catch (err) {
      console.error('Failed to extract signal types', err);
      // You might want to show a toast here
    } finally {
      setExtracting(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await klineMonitorService.getMonitorRuleLogs(page, pageSize);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch logs', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen, fetchLogs]);

  const handleOpenDetail = (title: string, content: string, type: LogExportFormat, extra?: any) => {
    setDetailDialog({
      isOpen: true,
      title,
      content,
      useMarkdown: type === 'markdown' || type === 'prompt',
      ...(type === 'prompt' ? extra : {}),
    });
  };

  if (!isOpen) return null;

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md" onClick={onClose}>
      <div
        className="bg-[#0F1115] rounded-2xl border border-white/10 w-full max-w-6xl h-[85vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex justify-between items-center bg-[#15181E]">
          <div>
            <h2 className="text-xl font-bold text-white tracking-tight">监控规则生成日志</h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExtractSignalTypes}
              disabled={extracting}
              className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-medium rounded-lg border border-blue-500/20 transition-all flex items-center gap-2"
            >
              {extracting ? (
                <>
                  <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  提取中...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                    />
                  </svg>
                  信号类型
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 bg-gradient-to-b from-[#0F1115] to-[#13151A]">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-full gap-3">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
              <div className="text-white/40 text-sm">正在加载日志数据...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-white/30 gap-4">
              <svg className="w-16 h-16 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span>暂无日志数据</span>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <LogItem key={log.id} log={log} onOpenDetail={handleOpenDetail} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex justify-between items-center bg-[#15181E]">
          <div className="text-sm text-white/40">
            共 <span className="text-white/80 font-mono mx-1">{total}</span> 条记录
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-2 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 rounded-lg text-white text-sm transition-all border border-white/5 flex items-center justify-center"
              title="上一页"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="px-4 py-2 text-white/60 text-sm font-mono bg-white/5 rounded-lg border border-white/5 min-w-[80px] text-center">
              {page} / {totalPages || 1}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="p-2 bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 rounded-lg text-white text-sm transition-all border border-white/5 flex items-center justify-center"
              title="下一页"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <ContentDialog
        visible={detailDialog.isOpen}
        title={detailDialog.title}
        content={detailDialog.content}
        systemPrompt={detailDialog.systemPrompt}
        userPrompt={detailDialog.userPrompt}
        useMarkdown={detailDialog.useMarkdown}
        onClose={() => setDetailDialog((prev) => ({ ...prev, isOpen: false }))}
      />

      {/* Extract Types Dialog */}
      {extractDialogOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md"
          onClick={() => setExtractDialogOpen(false)}
        >
          <div
            className="bg-[#15181E] rounded-xl border border-white/10 w-full max-w-lg shadow-2xl overflow-hidden m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-white/10 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-bold text-white">已提取的信号类型</h3>
                <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer hover:text-white select-none">
                  <input
                    type="checkbox"
                    checked={filterUndefined}
                    onChange={(e) => setFilterUndefined(e.target.checked)}
                    className="rounded border-white/20 bg-white/5 text-blue-500 focus:ring-0 focus:ring-offset-0 w-3.5 h-3.5"
                  />
                  未定义
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyAll}
                  className="p-1.5 text-white/40 hover:text-blue-400 hover:bg-white/5 rounded-lg transition-colors"
                  title="复制全部"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setExtractDialogOpen(false)}
                  className="p-1.5 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {extractedTypes.length === 0 ? (
                <div className="text-center text-white/40 py-8">未找到任何信号类型</div>
              ) : (
                <div className="space-y-2">
                  {extractedTypes
                    .filter((type) => {
                      if (!filterUndefined) return true;
                      const label = getSignalLabel(type);
                      return label === type || label === '-';
                    })
                    .map((type) => (
                      <div
                        key={type}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5 hover:bg-white/10 transition-colors"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-white">{type}</span>
                          <span className="text-xs text-white/40">{getSignalLabel(type)}</span>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${type} (${getSignalLabel(type)})`);
                          }}
                          className="p-1.5 text-white/20 hover:text-blue-400 transition-colors"
                          title="复制"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
            {showCopyToast && (
              <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-[70]">
                <div className="bg-black/90 backdrop-blur-md text-white px-4 py-2.5 rounded-xl border border-white/10 shadow-2xl flex items-center gap-3 animate-pulse">
                  <div className="bg-green-500/20 text-green-400 p-1 rounded-full">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium">已复制全部内容</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitorLogsDialog;
